import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import HTTP from 'services/http';
import uuidV4 from 'uuid/v4';
import { FCPaymentOptions } from 'constants/fc-refill-payment-choice';
import CompletedPage from './pages/completed-page';
import OrderProcessingPage from './pages/order-processing-page';
import InitialPage from './pages/initial-page';
import ChangesNeededPage from './pages/changes-needed-page';
import QuestionsPage from './pages/questions-page';
import FinalPage from './pages/final-page';
import EmptyOrderFinalPage from './pages/empty-order-final-page';
import InvalidPage from './pages/invalid-page';
import LoadingPage from './pages/loading-page';
import DataCollectPage from './pages/data-collect-page';
import ErrorPage from './pages/error-page';
import answers from './answer_template';
import TransitionPage from './pages/questionnaire-transition-page';
import SubmissionErrorPage from './pages/submission-error-page';
import postSubmissionErrorPage from './pages/post-submission-error-page';
import { STRINGS } from './fc_ss_constants';
import PostSubmissionErrorPage from './pages/post-submission-error-page';

const INITIAL_PAGE = 0;
const QUESTIONS_PAGE = 1;
const FINAL_PAGE = 2;
const CHANGES_NEEDED_PAGE = 3;
const INVALID_PAGE = 4;
const COMPLETED_PAGE = 6;
const DOB_MISMATCH_PAGE = 7;
const QUESTIONNAIRE_TRANSITION_PAGE = 8;
const DATA_COLLECT_PAGE = 9;
const EMPTY_ORDER_FINAL_PAGE = 10;
const ORDER_PROCESSING_PAGE = 11;
const POST_SUBMISSION_ERROR_PAGE = 12;
const ORDER_SUBMISSION_ERROR_PAGE = 13;

const ancillary_supplies_list = {
  sharps_container: false,
  alcohol_swabs: false,
  adhesive_bandages: false,
  oral_syringe: false,
  medicine_dispensing_cup: false,
  not_needed: false,
};

const isProduction = ['arbor.trellisrx.com', 'staging-arbor.trellisrx.com'].includes(
  window.location.hostname,
);
const Refills = () => {
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const { urlShorten } = useParams();
  const [patientAnswers, setPatientAnswers] = useState(answers);
  const [answerOptions, setAnswerOptions] = useState(null);
  const [pharmacyInfo, setPharmacyInfo] = useState({});
  const [patientDob, setPatientDob] = useState(null);
  const [authCount, setAuthCount] = useState(0);
  const [remainingAuthAttempts, setRemainingAuthAttempts] = useState(3);
  const [onlyAncillaryTherapies, setOnlyAncillaryTherapies] = useState(false);
  const [dataCollects, setDataCollects] = useState([]);

  const onError = useCallback(
    (message, ex) => {
      const uuid = uuidV4().substr(0, 6);
      if (error && error.message) {
        // eslint-disable-next-line no-console
        console.error(`${uuid} - ${ex.message} - ${ex.stack}`);
      } else {
        // eslint-disable-next-line no-console
        console.error(`${uuid} - ${ex}`);
      }
      setError({
        message,
        uuid,
      });
    },
    [error],
  );

  const setPageTypeFromData = data => {
    setLoading(false);
    if ((!data.isValid && !data.completedDateTime) || !data) {
      setPharmacyInfo(data.pharmacyInfo || {});
      setPage(INVALID_PAGE);
      return;
    }

    if (data.completedDateTime) {
      setPage(COMPLETED_PAGE);
      return;
    }

    if (!data.answerOptions) {
      if (data.error === STRINGS.dob_error) {
        setPage(DOB_MISMATCH_PAGE);
      } else {
        setRemainingAuthAttempts(data.remaining_auth_attempts);
        setPharmacyInfo(data.pharmacyInfo || {});
        setPage(INITIAL_PAGE);
      }
      return;
    }
    const only_ancillary_therapies = !data.answerOptions.therapies.some(
      therapy => therapy.is_specialty,
    );
    setOnlyAncillaryTherapies(only_ancillary_therapies);
    setPharmacyInfo(data.pharmacyInfo || {});
    setPatientAnswers({
      ...patientAnswers,
      therapies: data.answerOptions.therapies,
      ancillary_supplies: ancillary_supplies_list,
    });
    setAnswerOptions(data.answerOptions);
    if (data.dataCollects.length) {
      setDataCollects(data.dataCollects);
    }
    if (data.dataCollects.length) {
      setPage(QUESTIONNAIRE_TRANSITION_PAGE);
    } else {
      setPage(QUESTIONS_PAGE);
    }
  };

  const submitAnswers = async new_answers => {
    let payload;
    let original_answers;
    let workingAnswers;
    try {
      original_answers = { ...new_answers };
      setPatientAnswers(new_answers);
      delete new_answers.ancillary_supplies_page_visited;
      workingAnswers = { ...new_answers };
      if (new_answers.empty_order) {
        workingAnswers.ancillary_supplies = [];
        Object.keys(ancillary_supplies_list).forEach(
          item => (workingAnswers.ancillary_supplies[item] = null),
        );
      }
      // if order doesn't contain any cold chain or liquid therapies then
      // the related ancillary supplies should not be included in the order.
      if (!workingAnswers.therapies.some(therapy => therapy.included && therapy.is_cold_chain)) {
        workingAnswers.ancillary_supplies.sharps_container = null;
        workingAnswers.ancillary_supplies.alcohol_swabs = null;
        workingAnswers.ancillary_supplies.adhesive_bandages = null;
      }
      if (
        !workingAnswers.therapies.some(
          therapy => therapy.included && therapy.is_liquid && therapy.is_specialty,
        )
      ) {
        workingAnswers.ancillary_supplies.oral_syringe = null;
        workingAnswers.ancillary_supplies.medicine_dispensing_cup = null;
      }
      const orderContainsCAMed = workingAnswers.therapies.some(
        therapy => therapy.included && therapy.clinic_administered,
      );
      if (orderContainsCAMed) {
        // if the order contains a Clinic Administered med then the patient does not get the option
        // to answer any of the questions related to pickup or delivery.
        workingAnswers.pickup_or_delivery = null;
        workingAnswers.delivery_date = null;
        workingAnswers.delivery_method = null;
        workingAnswers.signature_required = null;
        workingAnswers.preferred_shipping_address = null;
      }
      if (workingAnswers.empty_order) {
        // if the patient removed all therapies then they could not
        // have answered any other questions, so force proper values.
        workingAnswers.payment_method_id = null;
        workingAnswers.pickup_date = null;
        workingAnswers.delivery_date = null;
        workingAnswers.delivery_method = null;
        workingAnswers.therapies = new_answers.therapies;
        workingAnswers.patient_requested_therapy_changes = 1;
        Object.keys(workingAnswers.ancillary_supplies).forEach(item => {
          workingAnswers.ancillary_supplies[item] = null;
        });
        delete workingAnswers.pickup_or_delivery;
        delete workingAnswers.empty_order;
        delete workingAnswers.clinical_responses;
      } else if (workingAnswers.patient_contact_required) {
        // if the patient removed a non-prn, specialty therapy then they could not
        // have answered any other questions, so force proper values.
        workingAnswers.payment_method_id = FCPaymentOptions.CallPatient;
        workingAnswers.pickup_date = null;
        workingAnswers.delivery_date = null;
        workingAnswers.delivery_method = null;
        workingAnswers.therapies = new_answers.therapies;
        workingAnswers.patient_requested_therapy_changes = 1;
        Object.keys(workingAnswers.ancillary_supplies).forEach(item => {
          workingAnswers.ancillary_supplies[item] = null;
        });
        delete workingAnswers.pickup_or_delivery;
        delete workingAnswers.clinical_responses;
      } else {
        if (workingAnswers.payment_method.answerOptionsIndex > -1) {
          workingAnswers.payment_method_id =
            answerOptions.payment[workingAnswers.payment_method.answerOptionsIndex].id;
        } else {
          workingAnswers.payment_method_id = workingAnswers.payment_method.answerOptionsIndex;
        }
        if (workingAnswers.pickup_or_delivery === STRINGS.delivery) {
          if (workingAnswers.preferred_shipping_address.answerOptionsIndex > -1) {
            const a_index = workingAnswers.preferred_shipping_address.answerOptionsIndex;
            const item = answerOptions.addresses[a_index];
            const { zip } = item;
            workingAnswers.delivery_state = item.state;
            if (answerOptions?.delivery_methods?.[zip]) {
              workingAnswers.delivery_method = answerOptions.delivery_methods[zip].method;
            } else {
              workingAnswers.delivery_method = null;
            }
            workingAnswers.preferred_shipping_address = `${answerOptions.patientName} - ${
              item.use
            } - ${item.line1}${item.line2 ? `, ${item.line2}` : ''}, ${item.city}, ${item.state}, ${
              item.zip
            }`;
            if (
              answerOptions?.delivery_dates &&
              answerOptions.delivery_dates[item.zip] &&
              answerOptions.delivery_dates[item.zip].length === 0
            ) {
              workingAnswers.no_avail_delivery_dates = 1;
              workingAnswers.delivery_date = null;
            }
          } else {
            workingAnswers.preferred_shipping_address = null;
            workingAnswers.delivery_date = null;
          }
          workingAnswers.pickup_date = null;
        } else if (workingAnswers.pickup_or_delivery === STRINGS.pickup) {
          workingAnswers.delivery_method = null;
          workingAnswers.signature_required = null;
          workingAnswers.preferred_shipping_address = null;
          workingAnswers.delivery_date = null;
        }
      }

      delete workingAnswers.payment_method;
      workingAnswers.dp_name = answerOptions.dp_name;
      workingAnswers.dp_npi = answerOptions.dp_npi;
      workingAnswers.dp_method = answerOptions.dp_method;

      payload = { dob: patientDob, answers: workingAnswers };
    } catch (err) {
      try {
        JSON.stringify(original_answers);
        JSON.stringify(workingAnswers);
        payload = {
          dob: patientDob,
          error_info: {
            submission: original_answers,
            working_answers: workingAnswers,
            error: err.message || 'Error processing patient answers',
          },
        };
      } catch (err) {
        payload = {
          dob: patientDob,
          error_info: {
            info: 'there was an error trying to build error message payload',
            error: err.message || 'Error building submission error',
          },
        };
      }
    }
    let start;
    try {
      HTTP.post(`/public/fc-patient-submit/${urlShorten}`, payload)
        .then(async response => {
          const duration = new Date().getTime() - start;
          if (duration < 2000) {
            await new Promise((a, _b) => {
              setTimeout(() => {
                a(true);
              }, 2000 - duration);
            });
          }
          if (response.data.submissionError) {
            setPage(ORDER_SUBMISSION_ERROR_PAGE);
          } else {
            setPage(FINAL_PAGE);
          }
        })
        .catch(_err => {
          setPage(POST_SUBMISSION_ERROR_PAGE);
        });
    } catch (e) {
      setPage(POST_SUBMISSION_ERROR_PAGE);
      return;
    }
    start = new Date().getTime();
    setPage(ORDER_PROCESSING_PAGE);
  };

  React.useEffect(() => {
    let payload = { patientDob };
    if (authCount === 0 && !isProduction) {
      const urlParams = new URLSearchParams(window.location.search);
      const forcedDob = urlParams.get('dob');
      if (forcedDob) {
        setPatientDob(forcedDob);
        payload = { patientDob: forcedDob };
      }
    }
    setLoading(true);
    HTTP.post(`/public/refill-questionnaires/${urlShorten}`, payload)
      .then(response => {
        try {
          const { data } = response;
          setPageTypeFromData(data);
        } catch (e) {
          onError('Failed to load the questionnaire', e);
        } finally {
          setReady(true);
        }
      })
      .catch(ex => {
        onError('The questionnaire was not found', ex);
      });
  }, [authCount, urlShorten]);

  if (error) {
    return <ErrorPage message={error.message} uuid={error.uuid} />;
  }
  if (!ready) {
    return <LoadingPage />;
  }

  const store = {
    pharmacyInfo,
    answerOptions,
    patientAnswers,
    patientDob,
    onlyAncillaryTherapies,
    dataCollects,
  };

  return (
    <>
      {(page === INITIAL_PAGE || page === DOB_MISMATCH_PAGE) && (
        <InitialPage
          updateDob={dob => {
            setPatientDob(dob);
          }}
          onNext={() => {
            setAuthCount(authCount + 1);
          }}
          remainingAuthAttempts={remainingAuthAttempts}
          dobValue={patientDob}
          store={store}
          hasBirthDateError={page === DOB_MISMATCH_PAGE}
        />
      )}
      {page === ORDER_PROCESSING_PAGE ? <OrderProcessingPage /> : null}
      {page === CHANGES_NEEDED_PAGE && <ChangesNeededPage store={store} submit={submitAnswers} />}
      {page === QUESTIONS_PAGE && (
        <QuestionsPage skipToPage={setPage} submit={submitAnswers} store={store} />
      )}
      {page === COMPLETED_PAGE && <CompletedPage />}
      {page === FINAL_PAGE && <FinalPage store={store} />}
      {page === INVALID_PAGE && <InvalidPage store={store} />}
      {page === QUESTIONNAIRE_TRANSITION_PAGE && (
        <TransitionPage
          continue={() => setPage(DATA_COLLECT_PAGE)}
          questionnaire_count={store?.dataCollects?.length || 0}
        />
      )}
      {page === DATA_COLLECT_PAGE && (
        <DataCollectPage setPage={setPage} data_collects={dataCollects} dob={patientDob} />
      )}
      {page === EMPTY_ORDER_FINAL_PAGE && (
        <EmptyOrderFinalPage store={store} submit={submitAnswers} />
      )}
      {page === ORDER_SUBMISSION_ERROR_PAGE ? <SubmissionErrorPage /> : null}
      {page === POST_SUBMISSION_ERROR_PAGE ? <PostSubmissionErrorPage store={store} /> : null}
      {loading && <LoadingPage />}
    </>
  );
};

export default Refills;
